import { MeasureController } from "../../MeasureController";
import { JudgmentResult, JudgmentResultEnum } from "../../types.client";
import { BatterySpecificGravityMeasure } from "../../../models/schemas/measures/misc.schema";
import { BATTERY_SPECIFIC_GRAVITY_THRESHOLDS } from "../../constants";

/**
 * 축전지 비중 컨트롤러
 * 문서 기준: 23. 축전지 비중 측정 기록표.docx
 *
 * 4단계 등급 판정 (SSOT: BATTERY_SPECIFIC_GRAVITY_THRESHOLDS):
 * - 완전충전 (1.260~1.280): GOOD
 * - 정상운전 (1.240~1.260): GOOD
 * - 충전부족 (1.220~1.240): CAUTION
 * - 방전상태 (1.200 이하): BAD
 * - 값 없음: NA
 */
export class BatterySpecificGravityController extends MeasureController<BatterySpecificGravityMeasure> {
  protected getDefaultData(): BatterySpecificGravityMeasure {
    return {
      standardValue: "",
      measuredValue: 0,
      unit: "S.G",
      photo: { checkUrl: "", resultUrl: "" },
      result: null,
      discussion: "",
      remark: "",
      createdAt: null,
    };
  }

  protected judge(): JudgmentResult {
    const measured = this.data.measuredValue ?? 0;

    // 측정값이 0이면 NA
    if (measured === 0) {
      return {
        result: JudgmentResultEnum.NA,
        discussion: this.generateDiscussion(JudgmentResultEnum.NA),
      };
    }

    const { fullyCharged, normalOperation, underCharged } = BATTERY_SPECIFIC_GRAVITY_THRESHOLDS;

    // 1. 완전충전 (1.260~1.280) → GOOD
    if (measured >= fullyCharged.min && measured <= fullyCharged.max) {
      return {
        result: JudgmentResultEnum.GOOD,
        discussion: this.generateDiscussion(JudgmentResultEnum.GOOD, "완전충전"),
      };
    }

    // 2. 정상운전 (1.240~1.260) → GOOD
    if (measured >= normalOperation.min && measured < normalOperation.max) {
      return {
        result: JudgmentResultEnum.GOOD,
        discussion: this.generateDiscussion(JudgmentResultEnum.GOOD, "정상운전"),
      };
    }

    // 3. 충전부족 (1.220~1.240) → CAUTION
    if (measured >= underCharged.min && measured < underCharged.max) {
      return {
        result: JudgmentResultEnum.CAUTION,
        discussion: this.generateDiscussion(JudgmentResultEnum.CAUTION, "충전부족"),
      };
    }

    // 4. 방전상태 (1.220 미만, 충전부족 하한 미달) → BAD
    if (measured < underCharged.min) {
      return {
        result: JudgmentResultEnum.BAD,
        discussion: this.generateDiscussion(JudgmentResultEnum.BAD, "방전상태"),
      };
    }

    // 5. 범위 외 (1.280 초과) → GOOD (과충전도 양호로 처리)
    return {
      result: JudgmentResultEnum.GOOD,
      discussion: this.generateDiscussion(JudgmentResultEnum.GOOD, "기준 초과"),
    };
  }

  protected generateDiscussion(result: "GOOD" | "CAUTION" | "BAD" | "NA", status?: string): string {
    const measured = this.data.measuredValue ?? 0;

    if (result === JudgmentResultEnum.NA) {
      return "측정값이 입력되지 않았습니다.";
    }

    const statusText = status ? ` (${status})` : "";

    if (result === JudgmentResultEnum.GOOD) {
      return `비중 ${measured.toFixed(3)}${statusText}은(는) 양호합니다.`;
    }

    if (result === JudgmentResultEnum.CAUTION) {
      return `비중 ${measured.toFixed(3)}${statusText}은(는) 충전이 필요합니다.`;
    }

    return `비중 ${measured.toFixed(3)}${statusText}은(는) 방전 상태로 즉시 충전이 필요합니다.`;
  }
}
